import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Phone, Sparkle, Copy, CheckCircle, Envelope } from "@phosphor-icons/react";

const OUTCOMES = [
  { v: "rdv", l: "RDV pris ✅" },
  { v: "rappeler", l: "À rappeler" },
  { v: "email", l: "Envoyer un email" },
  { v: "repondeur", l: "Répondeur / absent" },
  { v: "refus", l: "Pas intéressé" },
];
const OUTCOME_TITLE = {
  rdv: "Appel — RDV obtenu", rappeler: "Appel — à rappeler", email: "Appel — email à envoyer",
  repondeur: "Appel — répondeur", refus: "Appel — refus",
};

function scriptToText(s) {
  const lines = [];
  if (s.objectif) lines.push(`Objectif : ${s.objectif}`);
  if (s.ouverture) lines.push(`\nOuverture :\n${s.ouverture}`);
  if (s.contexte) lines.push(`\nContexte :\n${s.contexte}`);
  if (s.questions_decouverte?.length) lines.push(`\nQuestions de découverte :\n` + s.questions_decouverte.map((q) => `- ${q}`).join("\n"));
  if (s.objections?.length) lines.push(`\nObjections :\n` + s.objections.map((o) => `• ${o.objection}\n  → ${o.reponse}`).join("\n"));
  if (s.conclusion) lines.push(`\nConclusion :\n${s.conclusion}`);
  if (s.conseils?.length) lines.push(`\nConseils :\n` + s.conseils.map((c) => `- ${c}`).join("\n"));
  return lines.join("\n");
}

export default function CallScript({ c, reload, onUseInEmail }) {
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState("rdv");
  const [note, setNote] = useState("");
  const [recallDate, setRecallDate] = useState("");
  const [rdvAt, setRdvAt] = useState("");
  const [logging, setLogging] = useState(false);

  const generate = async (packageKey) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/companies/${c.id}/call-script`, packageKey ? { package_key: packageKey } : {});
      setScript(data);
    } catch { toast.error("Erreur de génération du script"); }
    finally { setLoading(false); }
  };

  const copyAll = () => { navigator.clipboard.writeText(scriptToText(script)); toast.success("Script copié"); };

  const toEmail = () => {
    const parts = [];
    parts.push(`Bonjour ${script.dirigeant ? script.dirigeant : "Madame, Monsieur"},`);
    if (script.contexte) parts.push(script.contexte);
    if (script.questions_decouverte?.length) parts.push(script.questions_decouverte[0]);
    if (script.rdv_slots) {
      parts.push(`Voici mes disponibilités : ${script.rdv_slots}. Quel créneau vous conviendrait le mieux ?`);
    } else {
      parts.push(script.conclusion || "Seriez-vous disponible pour un court échange, en visio ou autour d'un café, dans les prochains jours ?");
    }
    parts.push("Bien à vous,");
    onUseInEmail?.(parts.join("\n\n"));
    toast.success("Email pré-rempli — vérifiez avant l'envoi");
  };

  const logCall = async () => {
    setLogging(true);
    try {
      await api.post("/activities", {
        company_id: c.id, company_nom: c.nom, type: "appel",
        title: OUTCOME_TITLE[outcome] || "Appel", call_outcome: outcome,
        recall_date: outcome === "rappeler" ? recallDate || undefined : undefined,
        rdv_at: outcome === "rdv" ? rdvAt || undefined : undefined,
        content: note.trim() || `Issue : ${OUTCOMES.find((o) => o.v === outcome)?.l || outcome}`,
      });
      toast.success("Appel journalisé dans le CRM");
      setNote(""); setRecallDate(""); setRdvAt("");
      reload?.();
    } catch { toast.error("Erreur de journalisation"); }
    finally { setLogging(false); }
  };

  return (
    <div className="border border-[#bcd0ff] bg-[#f0f5ff]" data-testid="call-script">
      <div className="px-4 py-3 border-b border-[#bcd0ff] flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-head font-bold text-[#002FA7]">
          <Phone size={16} weight="fill" /> Script d'appel à froid
        </span>
        <button data-testid="generate-call-script" onClick={() => generate()} disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#002FA7] text-white px-3 py-1.5 hover:bg-blue-900 disabled:opacity-50">
          <Sparkle size={13} weight="fill" /> {loading ? "Génération…" : script ? "Régénérer" : "Générer (IA)"}
        </button>
      </div>

      {!script && !loading && (
        <p className="p-4 text-xs text-muted-foreground">
          Génère un script d'appel personnalisé (accroche, questions de découverte, réponses aux objections, prise de RDV) adapté à ce cabinet et au package suggéré.
        </p>
      )}

      {script && (
        <div className="p-4 space-y-3 text-sm" data-testid="call-script-content">
          {script.available_packages && (
            <div className="flex flex-wrap gap-1">
              {script.available_packages.map((p) => (
                <button key={p.key} data-testid={`call-package-${p.key}`} onClick={() => generate(p.key)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${p.key === script.package_key ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#bcd0ff] text-[#002FA7] hover:bg-[#dbe7ff]"}`}>
                  {p.name}
                </button>
              ))}
            </div>
          )}

          {script.objectif && (
            <div className="text-xs bg-white border border-[#bcd0ff] px-3 py-2 rounded">
              <span className="font-semibold text-[#002FA7]">Objectif : </span>{script.objectif}
            </div>
          )}

          <Section title="Ouverture" body={script.ouverture} />
          <Section title="Contexte" body={script.contexte} />

          {script.questions_decouverte?.length > 0 && (
            <div>
              <div className="overline text-[#002FA7] mb-1">Questions de découverte</div>
              <ul className="space-y-1">
                {script.questions_decouverte.map((q, i) => (
                  <li key={`q-${i}`} className="text-xs flex gap-1.5"><span className="text-[#002FA7]">•</span>{q}</li>
                ))}
              </ul>
            </div>
          )}

          {script.objections?.length > 0 && (
            <div>
              <div className="overline text-[#002FA7] mb-1">Réponses aux objections</div>
              <div className="space-y-2">
                {script.objections.map((o, i) => (
                  <div key={`obj-${i}`} className="text-xs bg-white border border-[#bcd0ff] rounded px-3 py-2" data-testid="call-objection">
                    <div className="font-semibold">« {o.objection} »</div>
                    <div className="text-muted-foreground mt-0.5">→ {o.reponse}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Section title="Conclusion (prise de RDV)" body={script.conclusion} />

          {script.conseils?.length > 0 && (
            <div className="text-[11px] text-muted-foreground italic">
              {script.conseils.map((cs, i) => <div key={`c-${i}`}>💡 {cs}</div>)}
            </div>
          )}

          <div className="flex items-center gap-4 flex-wrap">
            <button data-testid="copy-call-script" onClick={copyAll}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:underline">
              <Copy size={13} /> Copier tout le script
            </button>
            {onUseInEmail && (
              <button data-testid="call-script-to-email" onClick={toEmail}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:underline">
                <Envelope size={13} weight="fill" /> Transformer en email
              </button>
            )}
          </div>

          {/* Journalisation de l'appel */}
          <div className="pt-3 border-t border-[#bcd0ff] space-y-2">
            <div className="overline text-[#002FA7]">Après l'appel</div>
            <div className="flex gap-2">
              <select data-testid="call-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}
                className="flex-1 border border-[#bcd0ff] bg-white px-2 py-1.5 text-xs outline-none">
                {OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <button data-testid="log-call" onClick={logCall} disabled={logging}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#002FA7] text-white px-3 py-1.5 hover:bg-blue-900 disabled:opacity-50">
                <CheckCircle size={13} weight="fill" /> J'ai appelé
              </button>
            </div>
            <input data-testid="call-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Note d'appel (optionnel)…" className="w-full border border-[#bcd0ff] bg-white px-2 py-1.5 text-xs outline-none" />
            {outcome === "rappeler" && (
              <label className="block text-xs text-[#002FA7]">Date de rappel
                <input type="date" data-testid="call-recall-date" value={recallDate} onChange={(e) => setRecallDate(e.target.value)}
                  className="w-full border border-[#bcd0ff] bg-white px-2 py-1.5 text-xs outline-none mt-1" />
              </label>
            )}
            {outcome === "rdv" && (
              <label className="block text-xs text-[#002FA7]">Date &amp; heure du RDV
                <input type="datetime-local" data-testid="call-rdv-at" value={rdvAt} onChange={(e) => setRdvAt(e.target.value)}
                  className="w-full border border-[#bcd0ff] bg-white px-2 py-1.5 text-xs outline-none mt-1" />
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, body }) {
  if (!body) return null;
  return (
    <div>
      <div className="overline text-[#002FA7] mb-1">{title}</div>
      <p className="text-xs leading-relaxed whitespace-pre-line">{body}</p>
    </div>
  );
}
